import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import {
    MESSAGES,
    ORDER_BY_NUMBER_QUERY_PARAM,
    PAGE_SIZE,
} from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useDeferredValue, useMemo, useState } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { skipToken } from '@apollo/client';
import { RuntimeContext } from 'src/runtimeContext';
import { MainLayout } from 'src/modules/common/layouts/MainLayout';
import {
    getSensorsPageConfig,
    SensorsPageData,
    SensorsPageVariables,
} from 'src/sensors/list/SensorsPageMapping.ts';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { Query } from 'src/modules/common/components/Query/Query';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { ValveIcon } from 'src/modules/common/icons/ValveIcon.tsx';
import { prepareSensorAttributes } from 'src/sensors/list/prepareSensorAttributes.ts';
import {
    DirectionEnum,
    RegionsEnum,
    SensorsFilterPagePrimaryQueryVariables,
} from 'src/generated/operations.ts';
import { useOrderBy } from 'src/modules/common/hooks/useOrderBy.ts';
import { generateOrderItems } from 'src/utils/generateOrderItems.ts';
import { usePageChange } from 'src/modules/common/hooks/usePageChange.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { useImperial } from 'src/sensors/hooks/useImperial';
import { getFilterValuesAsString } from 'src/common/Breadcrumb/getFilterValuesAsString';
import clsx from 'clsx';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { FilterSidebarComponent } from 'src/common/FilterSidebarComponent/FilterSidebarComponent.tsx';
import { DeviceGrid, OrderItems } from 'src/common/DeviceGrid/DeviceGrid.tsx';
import { Device } from 'src/common/Device/Device.tsx';
import { DropdownField } from 'src/modules/common/components/DropdownField/DropdownField';
import { CheckboxHandler } from 'src/modules/common/types/CheckboxHandler.ts';
import { FilterEventDetails } from 'src/modules/common/types/FilterEventDetails.ts';
import { useLocationState } from 'src/sensors/useLocationState.ts';
import { SensorBreadcrumbs } from 'src/sensors/common/components/SensorBreadcrumbs/SensorBreadcrumbs';
import { PATHS } from 'src/sensors/common/types/Paths';
import { SensorsSelectSensorButton } from 'src/sensors/list/components/SensorsSelectSensorButton';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';
import { updatedLocationState } from 'src/common/updatedLocationState.ts';
import { FlowRangeFields } from 'src/sensors/FlowUnitsRadioButtons/FlowUnitsOptions.ts';
import { updateFilter } from 'src/common/updateFilter.ts';
import { Price } from 'src/common/Price/Price.tsx';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { useListDisplayMode } from 'src/common/hooks/useListDisplayMode';

const limit = PAGE_SIZE;

export const SensorsPage = () => {
    const { userSettings, mandatoryFilterConfig, currency } = useContext(ConfigContext);
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);
    const [locationState, setLocationState, searchParams, setSearchParams] = useLocationState();
    const pageConfig = useMemo(() => getSensorsPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const [isListDisplayMode, toggleDisplayMode] = useListDisplayMode();
    const view = isListDisplayMode ? 'list' : 'grid';

    const [
        filter,
        checkboxHandler,,
        filtersReset,
    ] = useFilterLogic(pageConfig.filterConfig, searchParams);

    const hasAdditionalFilter = useMemo(
        () => {
            if (pageConfig.filterConfig === null) {
                return false;
            }
            return getFilterValuesAsString(pageConfig.filterConfig, filter).length > 0;
        },
        [pageConfig.filterConfig, filter],
    );

    const currentPage = parseInt(locationState.sensorsPage, 10) || 1;

    const [orderByNumber, setOrderByNumber] = useOrderBy(ORDER_BY_NUMBER_QUERY_PARAM);

    const filterComposed = composeFilter(
        [
            ...mandatoryFilterConfig.config,
            ...pageConfig.filterConfig,
        ],
        {
            ...mandatoryFilterConfig.values,
            ...filter,
        },
        userSettings.currentLocale,
        ['MeasuredValues'],
    );

    const variables = useMemo((): { variables: SensorsPageVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }

        const offset = (currentPage - 1) * limit;

        return {
            variables: {
                filter: filterComposed,
                locale: userSettings.currentLocale,
                offset,
                limit,
                orderByNumber: [],
                orderByString: [],
                orderByPrice: orderByNumber,
            },
        };
    }, [filter, currentPage, filterComposed, userSettings.currentLocale, orderByNumber]);

    const filtersVariables = useMemo((): { variables: SensorsFilterPagePrimaryQueryVariables } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }

        return {
            variables: {
                region: userSettings.currentRegion,
                filter: filterComposed,
                locale: userSettings.currentLocale,
            },
        };
    }, [filter, userSettings, filterComposed]);

    const defVariables = useDeferredValue(variables);

    const setSensorPage = useCallback((newSearchParams: URLSearchParams, newPage: number) => updatedLocationState(
        newSearchParams,
        {
            ...locationState,
            sensorsPage: newPage.toFixed(),
        },
    ), [locationState]);

    const [setPage, onPageChange] = usePageChange(() => PATHS.Sensors, setSensorPage);

    const sensorsCheckboxHandler: CheckboxHandler = useCallback((detail: FilterEventDetails) => {
        const newFilter = checkboxHandler(detail);
        setPage(1, updateFilter(newFilter, searchParams));
        return newFilter;
    }, [setPage, checkboxHandler, searchParams]);

    const resetSensorFilters = useCallback(() => {
        setPage(1, filtersReset(pageConfig.filterConfig, searchParams));
    }, [setPage, filtersReset, pageConfig.filterConfig, searchParams]);

    const { isImperial, showSwitch } = useImperial();

    const onUnitChange = useCallback((currentUnit: string) => {
        setLocationState({
            ...locationState,
            isImperial: currentUnit === MESSAGES.imperialUnit,
        });
    }, [locationState, setLocationState]);

    const onFlowRangeSelected = useCallback((selectedFlowRange: string) => {
        setSearchParams(
            filtersReset(
                [[locationState.selectedFlowRange, '']],
                setLocationState({
                    ...locationState,
                    selectedFlowRange: selectedFlowRange as FlowRangeFields,
                }),
            ),
        );
    }, [filtersReset, locationState, setLocationState, setSearchParams]);

    const resetHandler = useCallback(() => {
        setLocationState({
            ...locationState,
            sensorLabel: '',
            selectedAccessories: [],
        });
    }, [locationState, setLocationState]);

    const isStale = defVariables !== variables;
    const [isFilterOpen, setFilterOpen] = useState(false);

    const [priceOrderItem = null] = generateOrderItems(
        [
            ...(userSettings.shoppingCenter !== null && currency !== null ? [
                `${String(userSettings.shoppingCenter)}_${currency}`,
            ] : []),
            ...(userSettings.shoppingCenter !== null ? [
                String(userSettings.shoppingCenter),
            ] : []),
        ],
        pageConfig?.orderItemsConfig,
        DirectionEnum.Asc,
    );

    return (
        <div className="vss-sensors">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.SensorsPageTitle}
                    navigation={null}
                    breadcrumbs={(
                        <SensorBreadcrumbs
                            currentPage={PATHS.Sensors}
                            resetHandler={locationState.sensorLabel ? resetHandler : undefined}
                            locationState={locationState}
                            searchParams={searchParams}
                        />
                    )}
                    selectionButton={!isRevitPlugin && (
                        <SelectionsButton
                            isMobile={isMobile}
                            searchParams={searchParams}
                            locationState={locationState}
                        />
                    )}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<SensorsPageData, SensorsPageVariables>
                        query={pageConfig.query}
                        variables={defVariables}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.list.results.length === 0) {
                                return (
                                    <div className={clsx({
                                        'vss-sensors-page': true,
                                        'vss-sensors-page--mobile': isMobile,
                                    })}
                                    >
                                        <FilterSidebarComponent
                                            label={MESSAGES.SensorsModalFilterLabel}
                                            filterConfigPrimary={pageConfig.filterConfigPrimary}
                                            filterConfigSecondary={pageConfig.filterConfigSecondary}
                                            queryPrimary={pageConfig.queryPrimary}
                                            genericFilterQuery={pageConfig.genericFilterQuery}
                                            variables={filtersVariables}
                                            checkboxHandler={sensorsCheckboxHandler}
                                            handleReset={resetSensorFilters}
                                            filter={filter}
                                            selectedFlowRange={locationState.selectedFlowRange}
                                            onFlowRangeSelected={onFlowRangeSelected}
                                            isFilterSidebarOpened={isFilterOpen}
                                            setFilterSidebarOpened={setFilterOpen}
                                        />

                                        <NoData/>
                                    </div>
                                );
                            }

                            return (
                                <div className={clsx({
                                    'vss-sensors-page': true,
                                    'vss-sensors-page--mobile': isMobile,
                                })}
                                >
                                    <FilterSidebarComponent
                                        label={MESSAGES.SensorsModalFilterLabel}
                                        filterConfigPrimary={pageConfig.filterConfigPrimary}
                                        filterConfigSecondary={pageConfig.filterConfigSecondary}
                                        queryPrimary={pageConfig.queryPrimary}
                                        genericFilterQuery={pageConfig.genericFilterQuery}
                                        variables={filtersVariables}
                                        checkboxHandler={sensorsCheckboxHandler}
                                        handleReset={resetSensorFilters}
                                        filter={filter}
                                        selectedFlowRange={locationState.selectedFlowRange}
                                        onFlowRangeSelected={onFlowRangeSelected}
                                        isFilterSidebarOpened={isFilterOpen}
                                        setFilterSidebarOpened={setFilterOpen}
                                    />

                                    <div className={clsx({
                                        'vss-sensors-page__list': true,
                                        'vss-sensors-page__list--stale': isStale,
                                    })}
                                    >
                                        <DeviceGrid
                                            isMobile={isMobile}
                                            hasAdditionalFilter={hasAdditionalFilter}
                                            currentPage={currentPage}
                                            pageSize={limit}
                                            total={data.list.count || 0}
                                            onPageChange={(p: number) => onPageChange(p, searchParams)}
                                            onToggleView={toggleDisplayMode}
                                            view={view}
                                            defaultOrderLabel={MESSAGES.Label}
                                            orderItems={[
                                                ...(priceOrderItem !== null ? [
                                                    [MESSAGES.DevicePricePerUnit, priceOrderItem[1], priceOrderItem[2]],
                                                ] : []) as OrderItems,
                                            ]}
                                            orderSelected={[
                                                ...orderByNumber,
                                            ]}
                                            onOrderByChange={(newOrder) => {
                                                setPage(
                                                    1,
                                                    setOrderByNumber(searchParams, newOrder),
                                                );
                                            }}
                                            additionalActions={showSwitch && (
                                                <DropdownField
                                                    renderOption={(value) => <I18n messageId={value}/>}
                                                    selectedValue={isImperial
                                                        ? MESSAGES.imperialUnit
                                                        : MESSAGES.metricUnit}
                                                    options={[MESSAGES.metricUnit, MESSAGES.imperialUnit]}
                                                    onChange={onUnitChange}
                                                    size="md"
                                                    shaded={false}
                                                />
                                            )}
                                            filterHandler={() => setFilterOpen(true)}
                                        >
                                            {data?.list.results.map((product) => {
                                                const price = getPriceValue(
                                                    product,
                                                    userSettings.shoppingCenter as keyof typeof product,
                                                    currency,
                                                );
                                                return {
                                                    product,
                                                    price,
                                                };
                                            }).map(({ product, price }) => (
                                                <Device
                                                    key={`${product.ID}_${product.Label}`}
                                                    defaultImage={<ValveIcon/>}
                                                    product={product}
                                                    collapsedAttributesPerColumn={1}
                                                    view={isMobile ? 'mobile' : view}
                                                    attributes={prepareSensorAttributes(
                                                        product,
                                                        userSettings.currentRegion === RegionsEnum.Am,
                                                        isImperial,
                                                        locationState.selectedFlowRange,
                                                    )}
                                                    price={(
                                                        <Price
                                                            price={price.value}
                                                            currency={price.currency}
                                                            locale={userSettings.currentLocale}
                                                        />
                                                    )}
                                                    selectButton={(
                                                        <SensorsSelectSensorButton
                                                            searchParams={updatedLocationState(
                                                                new URLSearchParams(searchParams.toString()),
                                                                {
                                                                    ...locationState,
                                                                    sensorLabel: product.Label,
                                                                    sensorsPage: currentPage.toString(),
                                                                    selectedAccessories: [],
                                                                    sent: false,
                                                                    qty: 1,
                                                                    checkoutComment: '',
                                                                },
                                                            )}
                                                            isMobile={isMobile}
                                                        />
                                                    )}
                                                />
                                            ))}
                                        </DeviceGrid>
                                    </div>
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>
        </div>
    );
};
